import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DayritLogo } from '../components/ui';

const FEATURES = [
  { icon: '📴', title: 'Works fully offline',    desc: 'Add, check off, and manage tasks with zero internet. Everything saves instantly to your device.' },
  { icon: '☁️', title: 'Syncs automatically',   desc: 'Sign in once and your offline tasks upload instantly. Switch devices without losing anything.' },
  { icon: '🎙️', title: 'Voice input',           desc: 'Tap the mic and speak your task aloud. Your words are transcribed instantly — hands-free task capture.' },
  { icon: '🔁', title: 'Recurring tasks',        desc: 'Set tasks to repeat daily, weekly, or monthly. Streaks reset, next due date advances automatically.' },
  { icon: '✅', title: 'Subtasks',               desc: 'Break any task into steps. Check them off one by one and watch the progress bar fill up.' },
  { icon: '🔥', title: 'Habit tracker',          desc: 'Build daily habits with a visual 7-day grid. See streaks grow and completion rates at a glance.' },
  { icon: '🍅', title: 'Pomodoro timer',         desc: 'Built-in 25/5 timer accessible from the sidebar. Tracks sessions and auto-advances phases.' },
  { icon: '📊', title: 'Analytics',              desc: 'See your completion rate, daily streaks, weekly chart, and performance by category.' },
  { icon: '🎨', title: '6 color themes',         desc: 'Dayrit, Ocean, Sunset, Forest, Purple, or Amber — change your accent color in one click.' },
];

const STEPS = [
  { emoji: '⚡', title: 'Open the app', desc: 'No account needed. Add your first task in seconds.' },
  { emoji: '📝', title: 'Build your list', desc: 'Set priorities, due dates, categories, and subtasks.' },
  { emoji: '🔐', title: 'Sign in anytime', desc: 'Create an account later. Tasks sync automatically.' },
];

function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      background: scrolled ? 'rgba(var(--surface-rgb, 255,255,255), 0.92)' : 'transparent',
      backdropFilter: scrolled ? 'blur(12px)' : 'none',
      borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
      transition: 'all 0.2s',
    }}>
      <div style={{ maxWidth: 1040, margin: '0 auto', padding: '0 clamp(16px,4vw,40px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
          <DayritLogo size={28} />
          <span style={{ fontSize: 16, fontWeight: 800, background: 'var(--brand-grad)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Dayrit</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link to="/login" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', textDecoration: 'none', padding: '8px 16px', borderRadius: 9, border: '1.5px solid var(--border)', background: 'var(--surface)', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
          >Sign in</Link>
          <Link to="/app" style={{ fontSize: 13, fontWeight: 700, color: 'white', textDecoration: 'none', padding: '8px 18px', borderRadius: 9, background: 'var(--brand-grad)', boxShadow: '0 2px 10px var(--accent-ring)', whiteSpace: 'nowrap' }}>
            Try free →
          </Link>
        </div>
      </div>
    </nav>
  );
}

function HeroBadge({ text }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--accent-light)', border: '1px solid var(--accent-ring)', borderRadius: 100, padding: '5px 14px', fontSize: 12, fontWeight: 600, color: 'var(--accent)', marginBottom: 24 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', display: 'inline-block', animation: 'pulse 2s ease-in-out infinite' }} />
      {text}
    </div>
  );
}

function MockTask({ title, priority, done, category }) {
  const PRI = { high: '#DC2626', medium: '#D97706', low: '#16A34A' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 6 }}>
      <div style={{
        width: 17, height: 17, borderRadius: '50%', flexShrink: 0,
        border: done ? 'none' : `1.5px solid ${PRI[priority]}`,
        background: done ? PRI[priority] : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {done && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3 5.5L8 1" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>}
      </div>
      <span style={{ flex: 1, fontSize: 13, color: done ? 'var(--text3)' : 'var(--text)', textDecoration: done ? 'line-through' : 'none', fontWeight: done ? 400 : 500 }}>{title}</span>
      {category && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 5, background: 'var(--accent-light)', color: 'var(--accent)' }}>{category}</span>}
    </div>
  );
}

export default function HomePage() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif", color: 'var(--text)' }}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        @keyframes floatSlow {
  0%, 100% { transform: translateY(0) rotate(-15deg); }
  50% { transform: translateY(-10px) rotate(-12deg); }
}
      `}</style>

      <NavBar />

      {/* ── Hero ── */}
      <section style={{ maxWidth: 1040, margin: '0 auto', padding: 'clamp(100px,12vw,140px) clamp(16px,4vw,40px) 80px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 'clamp(40px,6vw,80px)', alignItems: 'center' }}>
        <div>
          <HeroBadge text="Free forever · No sign-up required" />
          <h1 style={{ fontSize: 'clamp(34px,5vw,56px)', fontWeight: 900, color: 'var(--text)', letterSpacing: '-1.5px', lineHeight: 1.1, marginBottom: 18 }}>
            Tasks that work<br />
            <span style={{ background: 'var(--brand-grad)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>even offline</span>
          </h1>
          <p style={{ fontSize: 'clamp(15px,2vw,18px)', color: 'var(--text3)', lineHeight: 1.65, marginBottom: 32, maxWidth: 460 }}>
            Dayrit is a task manager that starts working the moment you open it — no account, no setup. Sign in later and everything syncs.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/app" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 28px', borderRadius: 12,
              background: 'var(--brand-grad)', color: 'white',
              fontSize: 15, fontWeight: 700, textDecoration: 'none',
              boxShadow: '0 6px 24px var(--accent-ring)', letterSpacing: '-0.2px',
            }}>
              Start for free
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </Link>
            <Link to="/register" style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '14px 28px', borderRadius: 12,
              border: '1.5px solid var(--border)', background: 'var(--surface)',
              color: 'var(--text2)', fontSize: 15, fontWeight: 600, textDecoration: 'none',
              transition: 'all 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
            >Create account</Link>
          </div>
          <p style={{ marginTop: 16, fontSize: 12, color: 'var(--text3)' }}>
            ✓ No credit card &nbsp;·&nbsp; ✓ Works instantly &nbsp;·&nbsp; ✓ Free forever
          </p>
        </div>

        {/* App preview */}
        <div style={{ animation: 'float 6s ease-in-out infinite' }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.1)', maxWidth: 360, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Today</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</div>
              </div>
              <svg width="40" height="40" viewBox="0 0 40 40" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="20" cy="20" r="14" fill="none" stroke="var(--border)" strokeWidth="3"/>
                <circle cx="20" cy="20" r="14" fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round" strokeDasharray="44 88"/>
              </svg>
            </div>
            <MockTask title="Review quarterly report" priority="high" category="Work" />
            <MockTask title="Morning run — 30 min" priority="medium" done />
            <MockTask title="Call dentist" priority="low" />
            <MockTask title="Buy groceries" priority="medium" category="Personal" />
            {/* Voice input hint in preview */}
            <div style={{ marginTop: 10, padding: '9px 12px', borderRadius: 9, border: '1px dashed var(--border)', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text3)', fontSize: 13 }}>
              <span style={{ fontSize: 16, opacity: 0.5 }}>+</span>
              <span style={{ flex: 1 }}>Add a task…</span>
              <span title="Voice input" style={{ fontSize: 15, opacity: 0.45 }}>🎙️</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: 'clamp(48px,6vw,80px) clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>HOW IT WORKS</p>
          <h2 style={{ fontSize: 'clamp(24px,3vw,34px)', fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.8px', marginBottom: 48 }}>
            Up and running in 30 seconds
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 24 }}>
            {STEPS.map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: 16, margin: '0 auto 14px', background: 'var(--accent-light)', border: '1px solid var(--accent-ring)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
                  {s.emoji}
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: 'var(--accent)', color: 'white', fontSize: 11, fontWeight: 800, marginBottom: 10 }}>{i + 1}</div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{s.title}</h3>
                <p style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.55 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features grid ── */}
      <section style={{ maxWidth: 1040, margin: '0 auto', padding: 'clamp(60px,7vw,96px) clamp(16px,4vw,40px)' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>FEATURES</p>
          <h2 style={{ fontSize: 'clamp(24px,3vw,34px)', fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.8px' }}>
            Everything you need, nothing you don't
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px,1fr))', gap: 16 }}>
          {FEATURES.map((f, i) => (
            <div key={i}
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '22px 20px', transition: 'all 0.2s', position: 'relative' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 4px 20px var(--accent-ring)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
            >
              {/* "Free" badge on voice input */}
              {f.title === 'Voice input' && (
                <span style={{ position: 'absolute', top: 14, right: 14, fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 5, background: 'var(--accent-light)', color: 'var(--accent)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>FREE</span>
              )}
              <div style={{ fontSize: 28, marginBottom: 12 }}>{f.icon}</div>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{f.title}</h3>
              <p style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Premium ── */}
      <section style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: 'clamp(48px,6vw,80px) clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'linear-gradient(135deg,#f59e0b22,#f9731622)', border: '1px solid #f59e0b44', borderRadius: 100, padding: '5px 14px', fontSize: 12, fontWeight: 700, color: '#D97706', marginBottom: 14 }}>
              ✨ Coming Soon
            </div>
            <h2 style={{ fontSize: 'clamp(24px,3vw,34px)', fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.8px', marginBottom: 10 }}>
              Dayrit Pro
            </h2>
            <p style={{ fontSize: 15, color: 'var(--text3)', maxWidth: 500, margin: '0 auto', lineHeight: 1.6 }}>
              Everything in Free, plus power features for people serious about their productivity.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {[
              { icon: '🤖', title: 'AI Task Breakdown', desc: 'Describe any goal and AI instantly splits it into actionable subtasks for you.', badge: 'AI' },
              { icon: '🗣️', title: 'Natural Language Input', desc: 'Type "call dentist tomorrow at 3pm high priority" and it just works.', badge: 'AI' },
              { icon: '📆', title: 'Calendar Sync', desc: 'Two-way sync with Google Calendar and Apple Calendar. Due dates appear on your calendar automatically.', badge: 'Sync' },
              { icon: '👥', title: 'Task Sharing', desc: 'Share tasks and projects with teammates or family. Assign, comment, and track together.', badge: 'Team' },
              { icon: '📎', title: 'File Attachments', desc: 'Attach files, images, and documents directly to tasks. Everything in one place.', badge: 'Pro' },
              { icon: '📈', title: 'Advanced Analytics', desc: 'Full productivity history, heatmaps, streaks over time, and weekly email reports.', badge: 'Pro' },
              { icon: '🔔', title: 'Smart Reminders', desc: 'Push notifications with snooze, location-based reminders, and recurring alerts.', badge: 'Pro' },
              { icon: '🎨', title: 'Custom Themes', desc: 'Unlimited accent colors, custom backgrounds, and your own CSS — make Dayrit yours.', badge: 'Pro' },
            ].map((f, i) => (
              <div key={i} style={{ background: 'var(--surface2, var(--bg))', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 24 }}>{f.icon}</span>
                  <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 5, background: f.badge === 'AI' ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : 'var(--accent-light)', color: f.badge === 'AI' ? 'white' : 'var(--accent)', letterSpacing: '0.04em' }}>{f.badge}</span>
                </div>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{f.title}</h3>
                <p style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6 }}>{f.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 36 }}>
            <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>
              Currently in development. Free plan stays free forever.
            </p>
            <button style={{ padding: '11px 28px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'transparent', color: 'var(--text2)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
            >
              Notify me when Pro launches →
            </button>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ maxWidth: 1040, margin: '0 auto', padding: '0 clamp(16px,4vw,40px) clamp(60px,7vw,96px)' }}>
        <div style={{ background: 'var(--brand-grad)', borderRadius: 20, padding: 'clamp(40px,6vw,64px) clamp(24px,5vw,56px)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          {/* <div style={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '10%', background: 'rgba(255,255,255,0.1)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -30, left: -30, width: 140, height: 140, borderRadius: '20%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} /> */}
          <div style={{
  position: 'absolute',
  top: -60,
  right: -60,
  opacity: 0.12,
  transform: 'rotate(-15deg)',
  pointerEvents: 'none',
  animation: 'float 10s ease-in-out infinite',
}}>
  <DayritLogo size={220} />
</div>

<div style={{
  position: 'absolute',
  bottom: -70,
  left: -70,
  opacity: 0.08,
  transform: 'rotate(20deg)',
  pointerEvents: 'none',
  animation: 'float 10s ease-in-out infinite reverse',
}}>
  <DayritLogo size={160} />
</div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🚀</div>
            <h2 style={{ fontSize: 'clamp(24px,3vw,36px)', fontWeight: 900, color: 'white', letterSpacing: '-0.8px', marginBottom: 10 }}>
              Ready to get organized?
            </h2>
            <p style={{ fontSize: 'clamp(14px,2vw,16px)', color: 'rgba(255,255,255,0.8)', marginBottom: 32, maxWidth: 480, margin: '0 auto 32px' }}>
              Open the app right now — no account needed. Your first task is 10 seconds away.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/app" style={{ background: 'white', color: 'var(--text)', textDecoration: 'none', padding: '14px 32px', borderRadius: 12, fontSize: 15, fontWeight: 800, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', letterSpacing: '-0.2px' }}>
                Open app — no sign up
              </Link>
              <Link to="/register" style={{ background: 'rgba(255,255,255,0.18)', border: '1.5px solid rgba(255,255,255,0.35)', color: 'white', textDecoration: 'none', padding: '14px 28px', borderRadius: 12, fontSize: 15, fontWeight: 600 }}>
                Create free account
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ borderTop: '1px solid var(--border)', padding: 'clamp(20px,3vw,28px) clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
            <DayritLogo size={20} />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Dayrit</span>
          </Link>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <Link to="/app"      style={{ fontSize: 12, color: 'var(--text3)', textDecoration: 'none' }}>Open App</Link>
            <Link to="/login"    style={{ fontSize: 12, color: 'var(--text3)', textDecoration: 'none' }}>Sign In</Link>
            <Link to="/register" style={{ fontSize: 12, color: 'var(--text3)', textDecoration: 'none' }}>Register</Link>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>Free forever · Built with ❤️</span>
            <a href="https://vincedaniel.com" target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12, color: 'var(--text3)', textDecoration: 'none', transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
            >Powered by 乃ﾉｲ</a>
          </div>
        </div>
      </footer>
    </div>
  );
}